import { signal } from '@preact/signals';
import type { CoreEvent, RunCommand } from '../../core/types';
import type { RunPhase, RunSummary, Snapshot, SnapshotPrimary, SnapshotSpell } from '../../core/run';
import type { DerivedChange, DerivedStats } from '../../core/stats';
import type { LevelUpCard } from '../../core/progression';
import type { DoorOffer } from '../../core/floor';
import { ZERO_STATS } from '../../core/types';
import type { Content, RewardType, Slot, Stats } from '../../data/schema';
import { loadContent } from '../../data/content';
import { CLASS_NAME } from '../classes';

const content = loadContent();
let toastId = 0;

export interface Toast { id: number; text: string; kind?: 'achievement'; }
/** Durée d'affichage (ms) d'un toast ordinaire et d'un toast de succès. */
const TOAST_MS = 2000, ACHIEVEMENT_TOAST_MS = 3000;

export const REWARD_TEXT: Record<RewardType, { title: string; detail: string }> = {
  kamas: { title: 'Kamas', detail: 'Une bourse de Kamas à la fin de la salle.' },
  xp: { title: 'Autel d’XP', detail: 'Un autel qui offre de l’expérience à la fin de la salle.' },
  marchand: { title: 'Marchand', detail: 'Un marchand ouvre boutique après le combat.' },
  coffre: { title: 'Coffre', detail: 'Un équipement offert à la fin de la salle.' },
  fontaine: { title: 'Fontaine de soin', detail: 'Soigne la moitié des PV en entrant.' },
  defi: { title: 'Défi', detail: 'Une vague de plus, monstres plus résistants, Kamas et XP doublés.' },
};

function itemName(id: string): string { return content.items[id]?.name ?? id; }

function toastText(e: CoreEvent): string | null {
  switch (e.type) {
    case 'levelUp': return `Niveau ${e.level}`;
    case 'waveStart': return `Vague ${e.wave} / ${e.total}`;
    case 'roomCleared': return 'Salle nettoyée';
    case 'heal': return e.amount > 0 && e.source !== 'lifesteal' && e.source !== 'summon' ? `+${Math.round(e.amount)} PV` : null;
    case 'itemGranted': return `Objet obtenu : ${itemName(e.itemId)}`;
    case 'bought': return `Acheté : ${itemName(e.itemId)}`;
    case 'roomEntered': return `Salle ${e.roomIndex + 1} / ${hudState.roomsTotal.value}`;
    default: return null;
  }
}

export const hudState = {
  hp: signal(0), hpMax: signal(1), level: signal(1), xp: signal(0), xpNext: signal(1), kamas: signal(0), wave: signal(0), waves: signal(0), cooldown: signal(0),
  phase: signal<RunPhase>('combat'), roomIndex: signal(0), roomsTotal: signal(6),
  className: signal(''), spells: signal<SnapshotSpell[]>([]), equipment: signal<Partial<Record<Slot, string>>>({}), rerolls: signal(1),
  doors: signal<DoorOffer[]>([]), cards: signal<LevelUpCard[]>([]), shop: signal<Snapshot['shop']>(null), boss: signal<Snapshot['boss']>(null),
  stats: signal<Stats>(ZERO_STATS), primary: signal<SnapshotPrimary | null>(null), derived: signal<DerivedStats | null>(null), statsOpen: signal(false),
  cardEffects: signal<DerivedChange[][]>([]),
  /** Autohit (mobile) : 'on' tant qu'un monstre est à portée, 'idle' sinon, null sans écran tactile. */
  autohit: signal<'on' | 'idle' | null>(null),
  toasts: signal<Toast[]>([]), ended: signal<RunSummary | null>(null), modifiers: signal<string[]>([]),
  fromSnapshot(s: Snapshot): void {
    if (s.player) {
      this.hp.value = s.player.hp; this.hpMax.value = s.player.hpMax; this.level.value = s.player.level; this.xp.value = s.player.xp; this.xpNext.value = s.player.xpNext;
      this.className.value = CLASS_NAME[s.player.classId] ?? s.player.className; this.kamas.value = s.player.kamas; this.cooldown.value = s.player.attackCooldown;
      this.spells.value = s.player.spells; this.equipment.value = s.player.equipment; this.rerolls.value = s.player.rerolls;
      this.stats.value = s.player.stats; this.primary.value = s.player.primary; this.derived.value = s.player.derived;
    }
    this.wave.value = s.wave; this.waves.value = s.waves;
    this.phase.value = s.phase;
    if (s.phase !== 'combat') this.statsOpen.value = false; // pas de superposition avec portes, cartes, boutique ou fin
    this.roomIndex.value = s.roomIndex; this.roomsTotal.value = s.roomsTotal;
    this.doors.value = s.doors; this.cards.value = s.cards; this.cardEffects.value = s.cardEffects; this.shop.value = s.shop; this.boss.value = s.boss;
  },
  pushEvents(events: CoreEvent[]): void {
    for (const e of events) {
      const text = toastText(e);
      if (!text) continue;
      this.addToast({ text }, TOAST_MS);
    }
  },
  /** Un toast « Succès : <nom> (+N Ogrines) » de 3 s par succès connu du contenu. */
  pushAchievements(ids: string[], c: Content = content): void {
    for (const id of ids) {
      const a = c.achievements[id];
      if (a) this.addToast({ text: `Succès : ${a.name} (+${a.ogrines} Ogrines)`, kind: 'achievement' }, ACHIEVEMENT_TOAST_MS);
    }
  },
  addToast(t: Omit<Toast, 'id'>, ms: number): void {
    const id = ++toastId;
    this.toasts.value = [...this.toasts.value, { id, ...t }].slice(-3);
    setTimeout(() => { this.toasts.value = this.toasts.value.filter((x) => x.id !== id); }, ms);
  },
  commandSink: null as ((c: RunCommand) => void) | null,
  send(c: RunCommand): void { this.commandSink?.(c); },
  end(summary: RunSummary): void { this.ended.value = summary; },
  reset(): void {
    this.ended.value = null; this.className.value = ''; this.toasts.value = []; this.modifiers.value = []; this.statsOpen.value = false;
    this.phase.value = 'combat'; this.roomIndex.value = 0; this.doors.value = []; this.cards.value = []; this.shop.value = null; this.boss.value = null; this.spells.value = []; this.equipment.value = {}; this.cardEffects.value = []; this.autohit.value = null;
  },
};
